<template>
  <div v-if="totalPages > 0" class="pagination-container">
    <ul class="pagination">
      <li class="page-previous">
        <nuxt-link
          v-if="currentPageNumber > 1"
          v-scroll-to="{ el: '#main', container: 'html, body', offset: 0 }"
          :to="urlForPageFunc(currentPageNumber - 1)"
        >
          <span class="icon">Previous page</span>
        </nuxt-link>
      </li>
      <template
        v-for="(deviceSize, deviceSizeIndex) in [
          { cls: 'hide-for-medium-up', windowSize: 0 },
          { cls: 'show-for-medium-up', windowSize: 2 },
        ]"
      >
        <li
          v-for="page in pagesToShow(deviceSize.windowSize)"
          :key="`${deviceSizeIndex}_${page.number}`"
          v-scroll-to="{ el: '#main', container: 'html, body', offset: 0 }"
          :class="{
            current: page.number === currentPageNumber,
            ellipsis: page.display === '...',
            [deviceSize.cls]: true,
          }"
        >
          <template v-if="page.number === currentPageNumber">
            <span class="show-for-sr">You're on page</span>
            <span class="current-page">{{ page.number }}</span>
          </template>
          <nuxt-link
            v-else-if="page.display !== '...'"
            :to="urlForPageFunc(page.number)"
            >{{ page.display }}</nuxt-link
          >
        </li>
      </template>
      <li class="page-next">
        <nuxt-link
          v-if="currentPageNumber < totalPages"
          v-scroll-to="{ el: '#main', container: 'html, body', offset: 0 }"
          :to="urlForPageFunc(currentPageNumber + 1)"
        >
          <span class="icon">Next page</span>
        </nuxt-link>
      </li>
    </ul>
  </div>
</template>

<script setup>
const props = defineProps({
  currentPageNumber: {
    type: Number,
    default: 0,
  },
  totalPages: {
    type: Number,
    default: 0,
  },
  urlForPageFunc: {
    type: Function,
    required: true,
  },
})

const genPagesToShowArray = (windowSize, totalPages, currentPageNum) => {
  /*
   -includes page numbers surrounding current page
   -always includes first and last page
   -adds ellipses
  */
  const rightWindowEnd = currentPageNum + windowSize
  const leftWindowStart = currentPageNum - windowSize
  return Array.from(Array(totalPages).keys())
    .map((n) => n + 1)
    .reduce((acc, n) => {
      if (
        // always show first, last, and current page number
        n === 1 ||
        n === totalPages ||
        n === currentPageNum ||
        // show current and a few pages after current one
        (n <= rightWindowEnd && n > currentPageNum) ||
        // show current and a few pages before current one
        (n >= leftWindowStart && n < currentPageNum) ||
        // if outside previous conditions and less than 2 extra pages in segment,
        //   just show it
        (n < currentPageNum && leftWindowStart - 1 < 3) ||
        (n > currentPageNum && totalPages - rightWindowEnd < 3)
      ) {
        acc.push({ number: n, display: n })
      } else if (
        // ellipsis cases - at least 2 available to collapse
        (n === 2 && leftWindowStart - 1 > 2) ||
        (n === totalPages - 1 && totalPages - rightWindowEnd > 2)
      ) {
        acc.push({ number: n, display: '...' })
      }
      return acc
    }, [])
}

const pagesToShow = (windowSize) => {
  return genPagesToShowArray(
    windowSize,
    props.totalPages,
    props.currentPageNumber,
  )
}
</script>
